import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useDataProvider, useLogin, useNotify, useTranslate } from "ra-core";
import { useForm, type SubmitHandler } from "react-hook-form";
import { Navigate, useNavigate } from "react-router";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Notification } from "@/components/admin/notification";
import { ConfirmationRequired } from "@/components/atomic-crm/login/ConfirmationRequired";
import { LoginSkeleton } from "@/components/atomic-crm/login/LoginSkeleton";
import type { CrmDataProvider } from "@/components/atomic-crm/providers/types";
import type { SignUpData } from "@/components/atomic-crm/types";
import { InputContrasena } from "./CampoContrasena";
import { PanelMarca } from "./PanelMarca";

/**
 * Alta del PRIMER administrador (copia adaptada de login/SignupPage.tsx de Atomic).
 * Solo se muestra mientras no exista ningún usuario: el trigger handle_new_user de la
 * base de datos hace admin al primero. Después, /sign-up redirige al login.
 */
export const RegistroInicial = () => {
  const queryClient = useQueryClient();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const navigate = useNavigate();
  const translate = useTranslate();
  const login = useLogin();
  const notify = useNotify();

  const { data: inicializado, isPending } = useQuery({
    queryKey: ["init"],
    queryFn: async () => dataProvider.isInitialized(),
  });

  const { isPending: creando, mutate } = useMutation({
    mutationKey: ["signup"],
    mutationFn: async (datos: SignUpData) => dataProvider.signUp(datos),
    onSuccess: (datos) => {
      login({ email: datos.email, password: datos.password, redirectTo: "/" })
        .then(() => {
          notify("nevora.registro.creado");
          queryClient.invalidateQueries({ queryKey: ["auth", "canAccess"] });
        })
        .catch((err) => {
          if (err.code === "email_not_confirmed") {
            navigate(ConfirmationRequired.path);
          } else {
            notify("nevora.registro.error_login", { type: "error" });
            navigate("/login");
          }
        });
    },
    onError: (error) => {
      notify(error.message, { type: "error" });
    },
  });

  const {
    register,
    handleSubmit,
    formState: { isValid },
  } = useForm<SignUpData>({ mode: "onChange" });

  if (isPending) return <LoginSkeleton />;
  if (inicializado) return <Navigate to="/login" />;

  const enviar: SubmitHandler<SignUpData> = (datos) => mutate(datos);

  return (
    <div className="min-h-screen flex">
      <div className="relative grid w-full lg:grid-cols-2">
        <PanelMarca />
        <div className="flex flex-col justify-center w-full p-6 lg:p-8">
          <div className="w-full space-y-6 lg:mx-auto lg:w-[360px]">
            <div>
              <p className="etiqueta mb-2">
                {translate("nevora.app.crm_interno")}
              </p>
              <h1 className="titulo-pagina">
                {translate("nevora.registro.titulo")}
              </h1>
              <p className="text-sm text-muted-foreground mt-3">
                {translate("nevora.registro.texto")}
              </p>
            </div>
            <form onSubmit={handleSubmit(enviar)} className="space-y-4">
              <div className="flex flex-col gap-2">
                <Label htmlFor="first_name">
                  {translate("crm.auth.first_name")}
                </Label>
                <Input
                  {...register("first_name", { required: true })}
                  id="first_name"
                  type="text"
                  autoComplete="given-name"
                  required
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="last_name">
                  {translate("crm.auth.last_name")}
                </Label>
                <Input
                  {...register("last_name", { required: true })}
                  id="last_name"
                  type="text"
                  autoComplete="family-name"
                  required
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="email">{translate("ra.auth.email")}</Label>
                <Input
                  {...register("email", { required: true })}
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="password">
                  {translate("ra.auth.password")}
                </Label>
                <InputContrasena
                  {...register("password", { required: true, minLength: 8 })}
                  id="password"
                  autoComplete="new-password"
                  required
                />
              </div>
              <Button
                type="submit"
                disabled={!isValid || creando}
                className="w-full mt-4"
              >
                {creando ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    {translate("nevora.registro.creando")}
                  </>
                ) : (
                  translate("nevora.registro.crear")
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
      <Notification />
    </div>
  );
};

RegistroInicial.path = "/sign-up";
